// ============================================================================
// Faire tenir le planning de la semaine sur UNE page
// ============================================================================
// La feuille est punaisée dans l'officine. Elle tient en largeur — le
// `min-width` de la grille est neutralisé à l'impression — mais pas en
// hauteur : passé une dizaine de salariés, la légende, puis des lignes de
// salariés, basculent sur une deuxième page. Une semaine affichée en deux
// morceaux ne se lit plus ; personne ne punaise la page 2 à côté.
//
// Le navigateur ne réduit pas une feuille tout seul : « ajuster à la page » est
// un geste manuel du dialogue, à un endroit différent selon le navigateur. On
// mesure donc la feuille nous-mêmes juste avant que le dialogue s'ouvre, dans
// un clone hors écran, et on pose la réduction dans `--edt-print-scale` — lue
// par `@media print` (`index.css`).
//
// ⚠️ On ÉLARGIT la mise en page avant de la réduire, au lieu de réduire tel
// quel. Réduire une feuille large de 1070 px à 78 % donne 838 px imprimés :
// un cinquième de la page reste blanc à droite alors que la grille, elle, est
// à l'étroit. On compose donc sur une largeur plus grande (que la réduction
// ramène exactement à la largeur de page), ce qui donne aussi plus de place aux
// colonnes — donc moins de retours à la ligne, donc une feuille plus courte.
// ============================================================================

// A4 paysage à 96 dpi (l'unité dans laquelle le navigateur pagine), moins les
// marges de `@page`. ⚠️ `MARGIN_MM` doit suivre `@page { margin }` d'`index.css`.
const MM = 96 / 25.4;
const MARGIN_MM = 7;
const PAGE_W = 297 * MM - 2 * MARGIN_MM * MM;
const PAGE_H = 210 * MM - 2 * MARGIN_MM * MM;
const BUDGET = PAGE_H - 1; // un pixel de marge : un arrondi ne coûte pas une page

// En dessous, la feuille tient sur une page mais ne se lit plus : les horaires
// sont déjà en 10 px à l'écran. On préfère alors deux pages honnêtes à une page
// illisible — et les lignes de salariés s'y coupent proprement, jamais au
// milieu d'une ligne (`break-inside: avoid`).
const MIN_SCALE = 0.62;

/** Hauteur qu'occupera la feuille si on la compose sur `width` pixels. */
function measureAt(sheet: HTMLElement, width: number): number {
  const holder = document.createElement('div');
  holder.setAttribute('aria-hidden', 'true');
  holder.style.cssText = `position:fixed; left:-10000px; top:0; width:${width}px;`;

  const clone = sheet.cloneNode(true) as HTMLElement;
  clone.removeAttribute('id'); // un id ne se duplique pas
  clone.classList.add('edt-mesure');

  holder.appendChild(clone);
  document.body.appendChild(holder);
  const height = clone.getBoundingClientRect().height;
  holder.remove();
  return height;
}

/**
 * Pose la réduction sur la feuille. Branché sur `beforeprint` : le bouton
 * « Imprimer » comme le Ctrl+P de l'utilisateur passent par là.
 */
export function fitSheetToOnePage() {
  const sheet = document.getElementById('edt-print');
  if (!sheet) return;
  releaseSheet();

  let layoutW = PAGE_W;
  let height = measureAt(sheet, layoutW);
  if (height <= BUDGET) return; // tient déjà telle quelle

  // Chercher la largeur de composition qui, une fois ramenée à la page, tient
  // en hauteur. Élargir raccourcit la feuille (moins de retours à la ligne),
  // donc la suite descend vite : deux tours suffisent en pratique.
  for (let i = 0; i < 3 && (height * PAGE_W) / layoutW > BUDGET; i++) {
    layoutW = (height * PAGE_W) / BUDGET;
    height = measureAt(sheet, layoutW);
  }

  const scale = PAGE_W / layoutW;
  if (height * scale > BUDGET || scale < MIN_SCALE) return; // deux pages, assumées

  // Sur `:root` et pas sur la feuille : la hauteur est lue par `.edt-page`, qui
  // est son PARENT — une variable descend, elle ne remonte pas.
  const root = document.documentElement.style;
  root.setProperty('--edt-print-width', `${Math.round(layoutW)}px`);
  root.setProperty('--edt-print-scale', String(scale));
  // La page est taillée sur la feuille RÉDUITE. Sans ça le navigateur compte la
  // hauteur d'avant et sort une 2ᵉ page. Le pixel en plus évite qu'un arrondi
  // rogne la bordure basse de la légende.
  root.setProperty('--edt-print-height', `${Math.ceil(height * scale) + 1}px`);
}

/** Rend la feuille à son état d'écran, une fois le dialogue refermé. */
export function releaseSheet() {
  const root = document.documentElement.style;
  root.removeProperty('--edt-print-width');
  root.removeProperty('--edt-print-scale');
  root.removeProperty('--edt-print-height');
}
